(ns app.front.ui.dnd
  "A wrapper for a list of items that the user can reorder by dragging and dropping.
  In this namespace 'source' means the item being dragged and 'target' means the item
  it is currently being dragged onto"
  (:require [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [goog.object :as go]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))

(def empty-image "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==")

(defn- dnd-group-from-id
  "Used for dnd:s whose items cannot be dragged to other dnd's (thus have a dnd-group of one member)"
  [dnd-id]
  (str dnd-id "-dnd-group"))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Subscriptions and events for storing info about the current drag-n-drop

;; State is a map with data related to the currently happening drag-n-drop:
;; Note that dnd-group is used here so dragging from one dnd to another is possible
;; {:source           the item currently being dragged
;;  :source-dnd-id    the id of the dnd the source item is being dragged from
;;  :target           the item the source is currently being dragged onto (from the same dnd-group only)
;;  :target-dnd-id    the id of the dnd the target is in; used to clean up when dragging through multiple dnd:s
;;  :img              empty image used to replace the item image following the mouse cursor during dragging
;;  :insert-before?   true if the source should be put before the target (only needed when dragging between dnd:s)
;;  :target-on-insert stored event handler for the target dnd since on-drag-end is triggered on the source}
(rf/reg-sub ::state
  (fn [db [_ dnd-group]]
    (get-in db [::dnd-state dnd-group])))


(rf/reg-event-db ::update-state
  (fn [db [_ dnd-group update-fn & update-args]]
    (apply update-in db [::dnd-state dnd-group] update-fn update-args)))

;; The internal order for each dnd component is stored separately
(rf/reg-sub ::order
  (fn [db [_ dnd-id]]
    (get-in db [::dnd-order dnd-id])))

(rf/reg-event-db ::update-order
  (fn [db [_ dnd-group old-target-dnd-id target-dnd-id source target]]
    ;; If we are dragging the item down the list, put it after the target, otherwise put it before
    (let [old-order      (get-in db [::dnd-order target-dnd-id])
          source-ix      (p/find-index old-order #{source})
          target-ix      (p/find-index old-order #{target})
          insert-before? (or (nil? source-ix) ; dragged from different dnd so index not found
                             (> source-ix target-ix))
          insert-fn      (if target
                           (if insert-before?
                             util/insert-before
                             util/insert-after)
                           ;; Target is empty; dragging first item to an empty dnd
                           #(cons source %))
          on-insert      (get-in db [::on-insert target-dnd-id])]
      ;; Remove the dragged item (source) from its previous position and add it to its new one;
      ;; when moving to another dnd, do NOT remove it from the original dnd (and the DOM)
      ;; but instead hide it so the event handler for on-drag-end still exists and triggers
      (-> (cond-> db
            (and old-target-dnd-id
                 (not= old-target-dnd-id target-dnd-id))
            (update-in [::dnd-order old-target-dnd-id] (partial remove #{source})))
          (update-in [::dnd-order target-dnd-id] #(remove #{source} %))
          (update-in [::dnd-order target-dnd-id] insert-fn source #{target})
          (assoc-in [::dnd-state dnd-group :insert-before?] insert-before?)
          (assoc-in [::dnd-state dnd-group :on-insert] on-insert)))))

(rf/reg-event-db ::reset-order
  (fn [db [_ dnd-id order]]
    (assoc-in db [::dnd-order dnd-id] order)))

(rf/reg-event-db ::init
  ;; Only replace the dynamic ordering when the contents list has permanently changed
  (fn [db [_ dnd-id dnd-group new-order on-insert]]
    (let [dragging? (-> db (get-in [::dnd-state dnd-group]) :source)
          changed?  (-> db (get-in [::dnd-order dnd-id]) set (not= (set new-order)))]
      (cond-> db
        on-insert                      (assoc-in [::on-insert dnd-id] on-insert)
        (and (not dragging?) changed?) (assoc-in [::dnd-order dnd-id] new-order)))))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Drag event handlers attached to the drag-n-droppable elements

(defn- on-drag-start
  "When dragging starts, hide dragging graphic, set the cursor and set the dragged item as the source"
  [{:keys [id dnd-group]} item event]
  (let [img (.createElement js/document "img")
        dt  (.-dataTransfer event)]
    (go/set img "src" empty-image)
    (.setDragImage dt img 0 0) ; set empty image as dragging graphic
    (go/set dt "effectAllowed" "move") ; changes cursor
    (>evt [::update-state dnd-group assoc :img img :source-dnd-id id :source item])))

(defn- on-drag-enter
  "When dragging over an item, update the target so that if the user releases the drag
  the source is placed next to the item."
  [{:keys [id dnd-group no-update?] {:keys [source target-dnd-id]} :state} item event]
  (.preventDefault event)
  (when (and (not no-update?)
             source
             (not= source item))
    (>evt [::update-state dnd-group assoc :target item :target-dnd-id id])
    (>evt [::update-order dnd-group target-dnd-id id source item])))

(defn- on-drag-end
  "When dragging ends, update the new ordering and clean up the stuff added in on-drag-start"
  [{:keys                                                                            [id dnd-group on-reorder no-update? items order]
    {:keys [source target source-dnd-id target-dnd-id img insert-before? on-insert]} :state}
   _event]
  ;; Inserting into empty dnd from another dnd
  (when (and target-dnd-id
             (nil? target)
             on-insert)
    (on-insert source))
  ;; Dragging within one dnd doesn't need explicit before/after info
  (when (and source
             target
             (not no-update?)
             ;; Not moved back to original spot
             (or (not= target-dnd-id source-dnd-id)
                 (not= items order)))
    (if (= dnd-group (dnd-group-from-id id))
      (on-reorder source target)
      (on-reorder source target insert-before?)))
  (some-> img .remove)
  (>evt [::update-state dnd-group dissoc :img :source :source-dnd-id :target :target-dnd-id :on-insert]))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Functions for adding dnd related attributes to hiccup element opts

(defn- augment-draggable-props
  "Draggable = any hiccup element that works as a handle for starting a drag-n-drop for the given item"
  [{:keys [id items always-show-handle?] {:keys [source-dnd-id target-dnd-id source]} :state :as ctx} draggable-opts item]
  (r/merge-props draggable-opts
                 {:draggable     true
                  :class         ["cursor-move"
                                  (when-not (or always-show-handle?
                                                (-> items rest seq))
                                    "hidden")]
                  :on-drag-start (partial on-drag-start ctx item)
                  :on-drag-end   (partial on-drag-end ctx)}
                 ;; Dragging from one dnd to another; the original is not removed
                 ;; (see ::update-order above for details) so hide it instead
                 (when (and target-dnd-id
                            (= id source-dnd-id)
                            (not= source-dnd-id target-dnd-id)
                            (= source item))
                   {:style {:display "none"}})))

(defn- augment-target-props
  "Target = any hiccup element that works as a drag-n-drop dropzone"
  [{:keys [props-for-source] {:keys [source]} :state :as ctx} target-opts item]
  (r/merge-props target-opts
                 (when (= source item) props-for-source)
                 {:on-drag-over  #(.preventDefault %)
                  :on-drag-enter (partial on-drag-enter ctx item)}))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Functions for use outside this namespace

(defn new-ix
  "Figures out how the other items' indexes ix should change
  when an element is moved within the list from from-ix to to-ix
  if you handle the ordering outside the component"
  [from-ix to-ix ix]
  (cond
    (= ix from-ix)       to-ix
    (and (> ix from-ix)
         (<= ix to-ix))  (dec ix)
    (and (>= ix to-ix)
         (< ix from-ix)) (inc ix)
    :else                ix))

(defn ordering-tx-for-entity
  "For a collection of entities with :db/id and :ordered/order,
  give a mapping id -> new-order when `source` is moved to `target`"
  [{source :db/id} {target :db/id}]
  {:from source :to target})

(defn wrap-on-order-changed
  "Returns a fn that can be internally used like on-reorder but externally calls on-order-changed
  with the newly ordered list instead of the [from to] tuple.
  Not compatible with dragging between multiple dnd's"
  [items on-order-changed]
  (fn [from to]
    (let [moved-right?   (< (:ordered/order from) (:ordered/order to))
          remove-from    (partial remove #(= from %))
          [before after] (split-at (cond-> (:ordered/order to)
                                     moved-right? inc)
                                   items)]
      (->> (concat (remove-from before) [from] (remove-from after))
           (map-indexed #(assoc %2 :ordered/order %1))
           vec
           on-order-changed))))

(s/defschema DnDCtx
  (st/merge
    {:id    s/Str
     :items [s/Any]}
    (st/optional-keys
      {:dnd-group           s/Keyword
       :props-for-source    (s/maybe {s/Any s/Any})
       :on-reorder          (s/maybe (s/pred fn?))
       :on-insert           (s/maybe (s/pred fn?))
       :no-update?          s/Bool
       :always-show-handle? s/Bool})))

(s/defn wrap-dnd
  "Wrap a component as a realtime-re-sorting dnd collection.

  Augment your DOM element props with `augment-draggable-props` and
  `augment-target-props`, render your list in the order of
  `:sorted`. When the ordering changes externally, just re-render
  with a new items list.

  For optional sorting, give nil as `:on-reorder`, and the `augment-*`
  functions will be nil too.

  `opts` is a map of
    `:id`                   is the unique identifier of this dnd used internally for storing the order
    `:dnd-group`            is an optional id which, if defined, allows the user to drag and drop
                            items between any dnd that has the same dnd-group
    `:items`                is a sequence of things
    `:props-for-source`     is a map to merge to the target element
                            which is being dragged
    `:on-reorder`           is an event handler that takes items from and to
                            and also takes insert-before? if `:dnd-group` is provided because
                            when reordering from one dnd to another we can't deduce whether the item
                            should be placed before or after the target item
    `:on-insert`            is an optional event handler that takes an item
                            it is triggered when a new item is dragged into an empty dnd from another dnd
    `:no-update?`           set to true if you don't want the `:sorted` and you
                            will not (can not) redraw the component during drag
    `:always-show-handle?`  set to true if you don't want to hide the drag handle element when there's
                            only one item, should be used especially when the whole element acts as a handle

  `component-fn` is a function that produces the component. It takes map of
    `:augment-draggable-props`  which is a fn [opts item] that augments the props
                                map for the DnD handle element
    `:augment-target-props`     which is a fn [opts item] that augments a dom props
                                map for the DnD target element
    `:sorted`                   is the list of items, but reordered according to the
                                ongoing DnD operation"
  [{:keys [id dnd-group items on-reorder on-insert no-update?] :as ctx} :- DnDCtx
   component-fn :- (s/pred fn?)]
  (let [dnd-group (or dnd-group (dnd-group-from-id id))]
    (when-not no-update?
      ;; Immediate dispatch so that fresh data is available in the let form below.
      ;; If you come up with a solution which avoids this discouraged dispatch, be welcome to fix it :).
      (rf/dispatch-sync [::init id dnd-group items on-insert]))
    (let [state (<sub [::state dnd-group])
          order (<sub [::order id])
          ctx   (assoc ctx :state state :order order :dnd-group dnd-group)]
      (component-fn
        (merge (when on-reorder
                 {:augment-draggable-props (partial augment-draggable-props ctx)
                  :augment-target-props    (partial augment-target-props ctx)})
               (when-not no-update?
                 {:sorted order}))))))
